import type {
  HomeScreenConfig,
  HomeScreenLayout,
  IconItem,
  IconKind,
  SmplifyExtension,
} from "./types";

/**
 * Backfill the `IconKind` verdict on Web Clip items that match a given URL
 * but don't yet have one persisted. Returns a *new* config when at least one
 * item was patched, or `null` when nothing needed updating (so callers can
 * skip onChange/setState and avoid render loops).
 *
 * Why URL-keyed?
 *   The probe runs inside `WebClipFavicon` which only knows the clip's URL,
 *   not its position in the layout (which can be on iPhone Pages, iPad Dock,
 *   or nested several folders deep). Patching by URL covers every occurrence
 *   in one pass without threading per-cell paths through every component.
 *
 * Why "missing only"?
 *   We never overwrite an existing verdict — if the user (or an import) has
 *   already committed to "favicon" or "generic", a transient probe should
 *   not flip it. Only `undefined` is treated as "needs backfill".
 */
export function backfillIconKindByUrl(
  config: Record<string, unknown>,
  url: string,
  kind: IconKind,
): Record<string, unknown> | null {
  if (!url || (kind !== "favicon" && kind !== "generic")) return null;

  let mutated = false;

  function visitItem(item: IconItem): IconItem {
    if (item.Type === "Folder") {
      let folderChanged = false;
      const nextPages = item.Pages.map((page) => {
        let pageChanged = false;
        const nextPage = page.map((child) => {
          const next = visitItem(child);
          if (next !== child) pageChanged = true;
          return next;
        });
        if (pageChanged) folderChanged = true;
        return pageChanged ? nextPage : page;
      });
      return folderChanged ? { ...item, Pages: nextPages } : item;
    }
    if (item.Type === "WebClip" && item.URL === url && !item.IconKind) {
      mutated = true;
      return { ...item, IconKind: kind };
    }
    return item;
  }

  function visitLayout(layout: HomeScreenLayout): HomeScreenLayout {
    let layoutChanged = false;
    const Pages = layout.Pages.map((page) => {
      let pageChanged = false;
      const nextPage = page.map((it) => {
        const next = visitItem(it);
        if (next !== it) pageChanged = true;
        return next;
      });
      if (pageChanged) layoutChanged = true;
      return pageChanged ? nextPage : page;
    });
    let Dock = layout.Dock;
    if (Array.isArray(Dock)) {
      let dockChanged = false;
      const nextDock = Dock.map((it) => {
        const next = visitItem(it);
        if (next !== it) dockChanged = true;
        return next;
      });
      if (dockChanged) {
        Dock = nextDock;
        layoutChanged = true;
      }
    }
    return layoutChanged ? { ...layout, Pages, Dock } : layout;
  }

  const cfg = config as unknown as HomeScreenConfig;
  const nextTopPages = Array.isArray(cfg.Pages)
    ? visitLayout({ Pages: cfg.Pages, Dock: cfg.Dock ?? [] })
    : null;

  const ext = cfg["x-smplify"] as SmplifyExtension | undefined;
  let nextExt: SmplifyExtension | undefined = ext;
  if (ext) {
    const variants: SmplifyExtension["variants"] = {};
    let extChanged = false;
    for (const ff of ["iphone", "ipad"] as const) {
      const v = ext.variants?.[ff];
      if (!v) continue;
      const nv = visitLayout(v);
      if (nv !== v) extChanged = true;
      variants[ff] = nv;
    }
    if (extChanged) {
      nextExt = { ...ext, variants };
    }
  }

  if (!mutated) return null;

  const out: Record<string, unknown> = { ...config };
  if (nextTopPages) {
    out.Pages = nextTopPages.Pages;
    out.Dock = nextTopPages.Dock ?? [];
  }
  if (nextExt) out["x-smplify"] = nextExt;
  return out;
}
